<template>
  <div class="header-container">
    <div class="header-top">
      <img alt="造好物" src="@/assets/images/logo-cn.webp" class="logo" @click="router.push('/')" />
      <div class="menu-container">
        <template v-for="menu in menuItems" :key="menu.id">
          <div
            class="menu-item"
            :class="{ 'menu-active': isMenuActive(menu) }"
            v-if="!menu.isGroup"
            :_href="menu.path"
            @click.stop="goTo(menu)"
          >
            <span :class="menu.icon"></span>
            <span>{{ menu.name }}</span>
          </div>
          <div v-else class="group-container">
            <div class="group-name">{{ menu.name }}</div>
            <div
              class="group-box"
              :class="{
                'menu-active': isMenuActive(subMenu),
                'history-box': subMenu.id === 51
              }"
              v-for="subMenu in menu.children"
              :key="subMenu.id"
              :_href="subMenu.path"
              @click="goTo(subMenu)"
            >
              <span :class="subMenu.icon"></span>
              <span class="sub-menu-name">{{ subMenu.name }}</span>
              <span class="hitems-arrow-right" v-if="subMenu.children"></span>
              <span
                class="unread-count"
                :class="{ 'unread-count-big': noticeStore.getUnReadCount > 9 }"
                v-if="subMenu.id === 52 && noticeStore.getUnReadCount > 0"
              >
                {{ noticeStore.getUnReadCount > 99 ? '99+' : noticeStore.getUnReadCount }}
              </span>

              <div class="sub-menu-container-wrapper" v-if="subMenu.children">
                <div class="sub-menu-container">
                  <div
                    class="sub-menu-item"
                    :class="{ 'sub-menu-active': isMenuActive(subSubMenu) }"
                    v-for="subSubMenu in subMenu.children"
                    :key="subSubMenu.id"
                    @click.stop="goTo(subSubMenu)"
                  >
                    {{ subSubMenu.name }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </template>
      </div>
      <AppTicket ref="appTicketRef" />
      <div class="user-container">
        <div class="unlogin-btn" v-if="!isLogin" @click="handleLogin">
          <span>登录</span>
          <div class="login-tips">
            <span>登录/注册得「想要」次数</span>
            <div class="tip2">
              <span>有机会解锁折扣好物！</span>
              <img src="@/assets/images/ticket.webp" alt="login-tips" />
            </div>
          </div>
        </div>
        <div class="login-container" v-else>
          <img class="user-avatar" id="header-user-avatar" :src="user.avatar" />
          <span class="user-name">{{ user.nickName }}</span>
          <!-- <div class="tooltip-msg">{{ user.nickName }}</div> -->
          <LoginOut :menu="userMenuItems" />
        </div>
      </div>
    </div>
    <div class="header-bottom">
      <div class="inner-area" id="header-more-info">
        <span class="hitems-sub-items"></span>
        <span class="text">更多信息</span>
        <CompanyInfo />
      </div>
    </div>
    <AppNotice
      :visible="showNotice"
      :type="props.type"
      :base="props.base"
      @close="showNotice = false"
    />
  </div>
</template>

<script setup lang="ts">
  import { onMounted, computed, ref, watchEffect, inject, onBeforeMount } from 'vue'
  import { useUserStore } from '@common/stores/userStore'
  import { useRouter, useRoute } from 'vue-router'
  import AppTicket from './components/AppTicket/index.vue'
  // import NewTicket from './components/NewTicket/index.vue'
  import { type MenuItem, type UserMenuItem, menuList, userMenuList, type EnterType } from './menu'
  import LoginService from '@common/components/AppLogin/login'
  import AppNotice from './components/AppNotice/index.vue'
  import CompanyInfo from './components/CompanyInfo/index.vue'
  import { useNoticeStore } from '@common/stores/useNotice'
  import { eventBus } from '@common/utils/eventBus'
  import LoginOut from './components/LoginOut/index.vue'
  import type { PropType } from 'vue'

  defineOptions({
    name: 'AppHeader'
  })

  const props = defineProps({
    type: {
      type: String as PropType<EnterType>,
      default: 'community'
    },
    base: {
      type: String,
      default: '' // 系统地址
    }
  })

  const router = useRouter()
  const route = useRoute()
  const userStore = useUserStore()
  const user = computed(() => userStore.getUser)
  const curMenu = ref<MenuItem>()
  const menuItems = ref()
  const userMenuItems = ref()
  const showNotice = ref(false)
  const showCompanyInfo = ref(false)
  const noticeStore = useNoticeStore()
  const isLogin = computed(() => userStore.isLogin)
  const $clearKeepCache = inject('clearKeepCache')
  const clearAllCatch = () => {
    $clearKeepCache && ($clearKeepCache as Function)()
  }

  // 根据传入的类型和基础路径修改菜单链接
  // 如果是ecommerce类型，则将type为community的菜单链接修改为基础路径
  // 如果是community类型，则将type为ecommerce的菜单链接修改为基础路径
  type Menu = MenuItem | UserMenuItem
  const modifyMenuLinks = (menuItems: Menu[], type: EnterType, base: string): Menu[] => {
    const targetType = type === 'ecommerce' ? 'community' : 'ecommerce'
    return menuItems.map(item => {
      let newItem = { ...item }
      if (newItem.type === targetType && newItem.path) {
        newItem.path = base + newItem.path
      }
      if (newItem.children) {
        newItem.children = modifyMenuLinks(newItem.children, type, base)
      }
      return newItem
    })
  }

  onBeforeMount(() => {
    menuItems.value = modifyMenuLinks(menuList, props.type, props.base)
    userMenuItems.value = modifyMenuLinks(userMenuList, props.type, props.base)
  })

  const appTicketRef = ref<InstanceType<typeof AppTicket>>()

  const goTo = (menu: MenuItem) => {
    // 先关闭奖励弹窗
    appTicketRef.value && appTicketRef.value.closeClaimRewards()
    eventBus.trigger('closeTicketDrawer')

    if (menu.path) {
      if (menu.path.startsWith('http://') || menu.path.startsWith('https://')) {
        // 全连接，使用链接方式跳转
        window.location.href = menu.path
      } else {
        clearAllCatch()
        // 普通 path，使用 router 跳转
        router.push(menu.path)
      }
    }

    if (menu.id === 52) {
      // 点击消息，如果未登录，则跳转登录
      if (userStore.isLogin) {
        showNotice.value = !showNotice.value
      } else {
        login(() => {
          userStore.setIsLogin(true)
          showNotice.value = true
        })
      }
    } else {
      showNotice.value = false
    }
  }

  // 判断菜单项是否激活
  const isMenuActive = (menu: MenuItem) => {
    if (showNotice.value) {
      return menu.id === 52
    }
    if (!menu.path) return false
    return route.path === menu.path || route.meta.group === menu.type
  }

  const login = (callback?: () => void) => {
    LoginService({
      type: 'login',
      onSuccess() {
        callback?.()
      }
    })
  }

  const handleLogin = () => {
    login(() => {
      window.location.href = '/'
    })
  }

  eventBus.addEvent('openHistory', () => {
    goTo({
      id: 51,
      name: '我的历史',
      path: '/my-history'
    })
  })

  // 关闭票的消费列表弹窗
  // const closeTicketConsumptionList = () => {
  //   emitter.emit(EventBusKey.closeTicketConsumptionList)
  // }
</script>

<style scoped lang="stylus">
  @import "./index.styl"
</style>
